import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react'
import { getToken, isAuthenticated, onTokenChange, redirectToSignIn, refreshToken } from '@/services/keycloakService'
import { loadModule, resolveModule } from './modules'

/** Attributes to set on the element; null or undefined removes one. */
export type ModuleAttributes = Record<string, string | null | undefined>

type Status =
  | { kind: 'loading' }
  | { kind: 'mounted' }
  | { kind: 'failed'; message: string }

/**
 * Mounts a registry module (logbook decision 15) as the page: resolves
 * `id` at `major`, loads it, and renders its element with the shell's session.
 * The contract is decision 14's: the host sets the attributes and answers the
 * events, and the module never reads the host's storage.
 *
 * - `token` is always the shell's current access token, re-set whenever it
 *   refreshes, so a module seldom sees one expire.
 * - `token-expired`: refresh and re-set `token`; a session that cannot be
 *   renewed goes back to sign-in.
 * - `error`: shown above the module, with Try again, which mounts it afresh.
 *
 * Anything else the page cares about (`reservation-created`, `cancelled`) is
 * in `events`.
 */
export function ModuleHost({
  id,
  major,
  label,
  attributes,
  events,
}: {
  id: string
  major: number
  /** What the person calls it, for the loading and error text. */
  label: string
  attributes: ModuleAttributes
  events?: Record<string, (event: CustomEvent) => void>
}) {
  const box = useRef<HTMLDivElement>(null)
  const element = useRef<HTMLElement | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState<Status>({ kind: 'loading' })
  // Reported by the module after it mounted; it stays on screen beneath.
  const [moduleError, setModuleError] = useState<string | null>(null)

  // Read through refs so a re-render never tears the element down.
  const latestAttributes = useRef(attributes)
  latestAttributes.current = attributes
  const latestEvents = useRef(events)
  latestEvents.current = events

  useEffect(() => {
    let cancelled = false
    let el: HTMLElement | null = null
    const listeners: [string, EventListener][] = []
    setStatus({ kind: 'loading' })
    setModuleError(null)

    const listen = (name: string, handler: (e: CustomEvent) => void) => {
      const listener = (e: Event) => handler(e as CustomEvent)
      el!.addEventListener(name, listener)
      listeners.push([name, listener])
    }

    resolveModule(id, major)
      .then(async (module) => {
        await loadModule(module)
        if (cancelled || !box.current) return
        el = document.createElement(module.element)
        el.style.display = 'block'
        el.style.height = '100%'
        apply(el, { ...latestAttributes.current, token: getToken() ?? null })

        listen('token-expired', async () => {
          if (await refreshToken()) {
            el?.setAttribute('token', getToken() ?? '')
          } else if (!isAuthenticated()) {
            redirectToSignIn()
          } else {
            // Still signed in, but the refresh did not go through (offline?).
            setModuleError('Your session could not be renewed. Check the connection and try again.')
          }
        })
        listen('error', (e) => {
          const message = (e.detail as { message?: unknown } | null)?.message
          setModuleError(typeof message === 'string' && message ? message : `The ${label} failed.`)
        })
        for (const name of Object.keys(latestEvents.current ?? {})) {
          listen(name, (e) => latestEvents.current?.[name]?.(e))
        }

        box.current.appendChild(el)
        element.current = el
        setStatus({ kind: 'mounted' })
      })
      .catch((e: unknown) => {
        if (!cancelled) setStatus({ kind: 'failed', message: e instanceof Error ? e.message : String(e) })
      })

    // Before the old token expires, not after: the module just uses the new one.
    const unsubscribe = onTokenChange(() => {
      const token = getToken()
      if (token && el) el.setAttribute('token', token)
    })

    return () => {
      cancelled = true
      unsubscribe()
      for (const [name, listener] of listeners) el?.removeEventListener(name, listener)
      el?.remove()
      element.current = null
    }
  }, [id, major, attempt, label])

  // Every render: set whatever changed. Cheap, and no attribute goes stale.
  useEffect(() => {
    if (element.current) apply(element.current, attributes)
  })

  const retry = () => setAttempt((n) => n + 1)

  return (
    <div className="relative h-full">
      <div ref={box} className="h-full" />

      {status.kind === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-muted-foreground" role="status">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading the {label}…</span>
        </div>
      )}

      {status.kind === 'failed' && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-3" role="alert">
            <AlertTriangle className="w-8 h-8 mx-auto text-destructive" />
            <p className="text-lg font-semibold">The {label} could not load.</p>
            <p className="text-sm text-muted-foreground break-words">{status.message}</p>
            <button
              type="button"
              onClick={retry}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium"
            >
              <RotateCw className="w-4 h-4" /> Try again
            </button>
          </div>
        </div>
      )}

      {status.kind === 'mounted' && moduleError && (
        <div
          role="alert"
          className="absolute top-2 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 max-w-[calc(100%-1rem)] rounded-md border border-destructive/40 bg-card px-3 py-2 shadow-lg"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 text-destructive" />
          <span className="text-sm min-w-0 break-words">{moduleError}</span>
          <button
            type="button"
            onClick={retry}
            className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm font-medium hover:bg-muted"
          >
            <RotateCw className="w-3.5 h-3.5" /> Try again
          </button>
        </div>
      )}
    </div>
  )
}

function apply(el: HTMLElement, attributes: ModuleAttributes): void {
  for (const [name, value] of Object.entries(attributes)) {
    if (value == null || value === '') {
      if (el.hasAttribute(name)) el.removeAttribute(name)
    } else if (el.getAttribute(name) !== value) {
      el.setAttribute(name, value)
    }
  }
}
